import { Permission } from '../utils/permissions';

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
  timestamp?: string;
}

// User and Auth types
export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  additionalPermissions?: Permission[];
  scopes?: OwnershipScope[];
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
}

export interface LoginRequest {
  email: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

// Entity types (matching backend Prisma schema)
export interface PlanningPeriod {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: PlanningPeriodStatus;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    investibleStructures: number;
    scenarios: number;
  };
}

export interface InvestibleStructure {
  id: string;
  name: string;
  description?: string;
  category: ISCategory;
  planningPeriodId: string;
  ownerId?: string;
  ownerText?: string;
  ownerType: OwnerType;
  createdAt: string;
  updatedAt: string;
  owner?: User;
  planningPeriod?: PlanningPeriod;
  epics?: Epic[];
  _count?: {
    epics: number;
  };
  demandTeamWeeks?: number;
  supplyTeamWeeks?: number;
  supplyAnnualCost?: number;
}

export interface Program {
  id: string;
  name: string;
  description?: string;
  category: ISCategory;
  leadId?: string;
  leadText?: string;
  leadType: LeadType;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lead?: User;
  teams?: Team[];
  _count?: {
    teams: number;
    epics: number;
    contributingEpics: number;
  };
}

export interface Team {
  id: string;
  name: string;
  programId: string;
  memberCount: number;
  costProfileId: string;
  teamLeadId?: string;
  teamLeadText?: string;
  teamLeadType: LeadType;
  teamLeadResourceLevelId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  program?: Program;
  costProfile?: CostProfile;
  teamLead?: User;
  costAllocations?: TeamCostAllocation[];
  activeMembers?: Array<{
    id: string;
    firstName: string;
    lastName: string;
    resourceLevelCode: string;
    resourceLevelId: string;
  }>;
  activeResourceCount?: number;
  _count?: {
    allocations: number;
  };
}

export interface TeamCostAllocation {
  id: string;
  teamId: string;
  costProfileId: string;
  resourceCount: number;
  createdAt: string;
  updatedAt: string;
  costProfile?: CostProfile;
}

export interface CostProfile {
  id: string;
  name: string;
  hourlyRate: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Epic {
  id: string;
  name: string;
  description?: string;
  businessOutcome?: string;
  requestedTeamWeeks: number;
  allocatedTeamWeeks: number;
  allocatedCost?: number;
  priority?: number;
  status: EpicStatus;
  programId?: string;
  isPrimaryProgram?: boolean;
  investibleStructureId: string;
  createdAt: string;
  updatedAt: string;
  investibleStructure?: InvestibleStructure;
  program?: Program;
  features?: Feature[];
  epicAllocations?: EpicAllocation[];
  milestones?: Milestone[];
  _count?: {
    features: number;
    epicAllocations?: number;
    milestones?: number;
  };
}

export interface Milestone {
  id: string;
  epicId: string;
  targetDate: string;
  completionPercentage: number;
  description?: string;
  type: MilestoneType;
  createdAt: string;
  updatedAt: string;
  epic?: Epic;
}

// Timeline-specific types
export interface TimelineEpic extends Epic {
  objectives: Objective[];
  milestones: Milestone[];
  timelineMetadata?: {
    startDate?: string;
    endDate?: string;
    durationDays: number;
  };
}

export interface Feature {
  id: string;
  name: string;
  description?: string;
  estimatedTeamWeeks?: number;
  leadProgramId?: string;
  epicId: string;
  targetQuarter: number;
  readinessScore: number;
  status: FeatureStatus;
  createdAt: string;
  updatedAt: string;
  epic?: Epic;
  leadProgram?: Program;
}

export interface OKR {
  id: string;
  title: string;
  description?: string;
  epicId: string;
  targetValue: number;
  currentValue: number;
  unit: string;
  dueDate?: string;
  status: OKRStatus;
  progress: number;
  createdAt: string;
  updatedAt: string;
  epic?: Epic;
}

// OKR Level type - determines which parent entity the objective belongs to
export type OKRLevel = 'IS' | 'EPIC' | 'PROGRAM';

// OKR Status enum matching redesigned backend
export enum OKRStatusNew {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// Objective and Key Result types (matching redesigned backend schema)
export interface Objective {
  id: string;
  title: string;
  description?: string;

  // Flexible parent - one of these will be set
  epicId?: string | null;
  investibleStructureId?: string | null;
  programId?: string | null;

  // OKR Cycle
  quarter: number;
  fiscalYear: number;

  // Status
  status: OKRStatusNew;

  // Alignment (optional parent objective)
  parentObjectiveId?: string | null;
  parentObjective?: { id: string; title: string; status?: OKRStatusNew } | null;
  childObjectives?: Objective[];

  // Computed level
  level?: OKRLevel;

  // Computed progress
  overallProgress?: number | null;

  // Entity name for display
  entityName?: string;

  // Relations
  epic?: Epic | null;
  investibleStructure?: InvestibleStructure | null;
  program?: Program | null;
  keyResults?: KeyResult[];

  // Counts
  _count?: {
    keyResults: number;
    childObjectives: number;
  };

  createdAt: string;
  updatedAt: string;
}

export interface KeyResult {
  id: string;
  description: string;
  targetValue: number;
  currentValue?: number | null;
  startValue: number;
  unit?: string | null;

  // Confidence scoring
  confidence?: number | null;

  // Computed progress
  progress?: number | null;

  objectiveId: string;
  objective?: Objective;

  // Progress history
  progressUpdates?: KeyResultProgress[];

  createdAt: string;
  updatedAt: string;
}

export interface KeyResultProgress {
  id: string;
  keyResultId: string;
  value: number;
  note?: string | null;
  progress?: number | null;
  recordedAt: string;
  recordedById: string;
  recordedBy?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
  };
}

// OKR Dashboard types
export interface OKRDashboardData {
  summary: {
    totalObjectives: number;
    totalKeyResults: number;
    avgOverallProgress: number | null;
    completionRate: number;
    byStatus: Record<string, number>;
  };
  byLevel: OKRLevelMetrics[];
  byQuarter: OKRQuarterlyMetrics[];
  alignment: OKRAlignmentMetrics;
  topObjectives: OKRTopObjective[];
  atRisk: OKRAtRiskObjective[];
}

export interface OKRLevelMetrics {
  level: OKRLevel;
  totalObjectives: number;
  byStatus: Record<string, number>;
  avgProgress: number | null;
  keyResultsCount: number;
  completionRate: number;
}

export interface OKRQuarterlyMetrics {
  quarter: number;
  fiscalYear: number;
  totalObjectives: number;
  avgProgress: number | null;
  byStatus: Record<string, number>;
  byLevel: Record<string, number>;
}

export interface OKRAlignmentMetrics {
  totalAligned: number;
  alignedToIS: number;
  alignedToProgram: number;
  orphanObjectives: number;
}

export interface OKRTopObjective {
  id: string;
  title: string;
  level: OKRLevel;
  entityName: string;
  status: OKRStatusNew;
  progress: number | null;
  keyResultsCount: number;
  quarter: number;
  fiscalYear: number;
}

export interface OKRAtRiskObjective {
  id: string;
  title: string;
  level: OKRLevel;
  entityName: string;
  status: OKRStatusNew;
  progress: number | null;
  daysInQuarter: number;
  expectedProgress: number;
}

export interface OKRCycle {
  fiscalYear: number;
  quarter: number;
  label: string;
  objectiveCount: number;
}

// OKR Tree types
export interface OKRTreeNode extends Objective {
  alignedChildren: OKRTreeNode[];
}

export interface OKRTree {
  isObjectives: OKRTreeNode[];
  programObjectives: OKRTreeNode[];
  epicObjectives: OKRTreeNode[];
  allAlignedObjectives: Objective[];
}

export interface Dependency {
  id: string;
  name: string;
  description?: string;
  type: DependencyType;
  sourceType: EntityType;
  sourceId: string;
  targetType: EntityType;
  targetId: string;
  status: DependencyStatus;
  severity: DependencySeverity;
  expectedDate?: string;
  resolvedDate?: string;
  createdAt: string;
  updatedAt: string;
  sourceName?: string;
  targetName?: string;
  relationship?: 'outgoing' | 'incoming';
  riskLevel?: 'OVERDUE' | 'DUE_SOON' | 'AT_RISK';
  daysUntilDue?: number;
}

export interface EpicAllocation {
  id: string;
  epicId: string;
  programId: string;
  teamId: string;
  allocatedTeamWeeks: number;
  quarter?: number;
  createdAt: string;
  updatedAt: string;
  epic?: Epic;
  program?: Program;
  team?: Team;
}

// Enums (matching backend)
export enum UserRole {
  ADMIN = 'ADMIN',           // Full system access
  EXECUTIVE = 'EXECUTIVE',
  IS_OWNER = 'IS_OWNER',
  PROGRAM_LEAD = 'PROGRAM_LEAD',
  PMO = 'PMO',
  FINANCE = 'FINANCE',
  VIEWER = 'VIEWER',
}

export enum OwnershipScope {
  IS_OWNER_SCOPE = 'IS_OWNER_SCOPE',
  PROGRAM_LEAD_SCOPE = 'PROGRAM_LEAD_SCOPE',
  TEAM_LEAD_SCOPE = 'TEAM_LEAD_SCOPE',
  DIVISION_LEAD_SCOPE = 'DIVISION_LEAD_SCOPE',
}

export enum PlanningPeriodStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED',
}

export enum ISCategory {
  BUSINESS = 'BUSINESS',
  TECH = 'TECH',
  SUPPORT = 'SUPPORT',
}

export enum EpicStatus {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  PRIORITIZED = 'PRIORITIZED',
  COMMITTED = 'COMMITTED',
  STRETCH = 'STRETCH',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
}

export enum FeatureStatus {
  NOT_READY = 'NOT_READY',
  IN_REFINEMENT = 'IN_REFINEMENT',
  SHOVEL_READY = 'SHOVEL_READY',
  COMPLETE = 'COMPLETE',
}

export enum OKRStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  AT_RISK = 'AT_RISK',
}

export enum DependencyType {
  BLOCKING = 'BLOCKING',
  ENABLING = 'ENABLING',
  INFORMATIONAL = 'INFORMATIONAL',
}

export enum DependencyStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN_PROGRESS',
  RESOLVED = 'RESOLVED',
  CANCELLED = 'CANCELLED',
}

export enum DependencySeverity {
  CRITICAL = 'CRITICAL',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export enum EntityType {
  PLANNING_PERIOD = 'PLANNING_PERIOD',
  INVESTIBLE_STRUCTURE = 'INVESTIBLE_STRUCTURE',
  EPIC = 'EPIC',
  FEATURE = 'FEATURE',
  PROGRAM = 'PROGRAM',
  TEAM = 'TEAM',
  COST_PROFILE = 'COST_PROFILE',
  ALLOCATION = 'ALLOCATION',
  FEATURE_ALLOCATION = 'FEATURE_ALLOCATION',
  SCENARIO = 'SCENARIO',
  OBJECTIVE = 'OBJECTIVE',
  KEY_RESULT = 'KEY_RESULT',
  FEATURE_READINESS = 'FEATURE_READINESS',
  DEPENDENCY = 'DEPENDENCY',
  USER = 'USER',
}

export enum NotificationType {
  INFO = 'INFO',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

export enum OwnerType {
  USER = 'USER',
  TEXT = 'TEXT',
}

export enum LeadType {
  USER = 'USER',
  TEXT = 'TEXT',
}

export enum MilestoneType {
  BUSINESS = 'BUSINESS',
  TECH = 'TECH',
}

// Resource Management Enums
export enum ResourceType {
  CONTRACTOR = 'CONTRACTOR',
  GDP_ONSHORE = 'GDP_ONSHORE',
  GDP_OFFSHORE = 'GDP_OFFSHORE',
  EMPLOYEE = 'EMPLOYEE',
  INTERN = 'INTERN',
}

export enum ResourceLevelCategory {
  TECHNICAL = 'TECHNICAL',
  MANAGEMENT = 'MANAGEMENT',
  SPECIALIZED = 'SPECIALIZED',
}

export interface ResourceLevel {
  id: string;
  code: string;
  displayName: string;
  description?: string;
  category: ResourceLevelCategory;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    rateCards: number;
    resources: number;
    resourceIntakeRequests: number;
  };
}

export enum RequestType {
  HEADCOUNT = 'HEADCOUNT',
  SOW = 'SOW',
  T_AND_M = 'T_AND_M',
}

export enum RequestStatus {
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
}

export enum ValidationStatus {
  PENDING = 'PENDING',
  VALID = 'VALID',
  WARNING = 'WARNING',
  INVALID = 'INVALID',
}

// Resource Management Types
export interface Division {
  id: string;
  name: string;
  leadId: string | null;
  leadText: string | null;
  leadType: LeadType;
  budgetCode: string | null;
  annualBudget: number | null;
  budgetFiscalYear: number | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lead?: User;
  programs?: Program[];
  _count?: {
    programs: number;
  };
}

export interface Resource {
  id: string;
  resourceId: string;
  firstName: string;
  lastName: string;
  resourceType: ResourceType;
  resourceLevelId: string;
  resourceLevel?: ResourceLevel;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  teamAssignments?: ResourceTeamAssignment[];
  _count?: {
    teamAssignments: number;
  };
}

export interface ResourceTeamAssignment {
  id: string;
  resourceId: string;
  teamId: string;
  startDate: string;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
  resource?: Resource;
  team?: Team;
}

export interface RateCard {
  id: string;
  resourceLevelId: string;
  resourceLevel?: ResourceLevel;
  year: number;
  hourlyRate: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ResourceIntakeRequest {
  id: string;
  requestType: RequestType;
  description: string;
  resourceType: ResourceType;
  resourceLevelId: string;
  resourceLevel?: ResourceLevel;
  estimatedCost: number | null;
  businessJustification: string;
  submitDate: string;
  status: RequestStatus;
  requestedById: string;
  approverId: string;           // Designated approver (required at submission)
  approvedById: string | null;  // Who actually approved/declined (after action)
  approvedDate: string | null;
  createdAt: string;
  updatedAt: string;
  requestedBy?: User;
  approver?: User;              // Designated approver details
  approvedBy?: User;            // Who actually approved/declined

  // Budget validation fields
  programId: string | null;
  program?: {
    id: string;
    name: string;
    laborBudget?: number | null;
  };
  annualCost: number | null;
  budgetImpactPercent: number | null;
  validationStatus: ValidationStatus;
  validationNotes: string | null;
  calculatedHourlyRate: number | null;
}

export interface ValidationResult {
  status: ValidationStatus;
  notes: string;
  annualCost: number | null;
  hourlyRate: number | null;
  budgetImpactPercent: number | null;
  remainingBudgetBefore: number | null;
  remainingBudgetAfter: number | null;
  programName?: string;
}

export interface BudgetImpactResult {
  programId: string;
  programName: string;
  laborBudget: number | null;
  annualBudget: number | null;
  approvedRequestsCost: number;
  pendingRequestsCost: number;
  existingTeamLaborCost: number;
  totalCommittedCost: number;
  remainingBudget: number | null;
  budgetUtilizationPercent: number | null;
  pendingRequestsCount: number;
  approvedRequestsCount: number;
}

// Notification types
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  isRead: boolean;
  readAt?: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationSummary {
  total: number;
  unread: number;
  read: number;
  byType: Record<NotificationType, number>;
}

export interface NotificationPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  epicStatusChanges: boolean;
  dependencyUpdates: boolean;
  capacityAlerts: boolean;
  readinessGateViolations: boolean;
  weeklyDigest: boolean;
}

// Dashboard types
export interface DashboardKPIs {
  totalInvestment: {
    dollars: number;
    teamWeeks: number;
    change: number;
  };
  q1Readiness: {
    percentage: number;
    readyFeatures: number;
    totalFeatures: number;
    status: 'on_track' | 'at_risk' | 'off_track';
  };
  activeEpics: {
    total: number;
    onTrack: number;
    atRisk: number;
    offTrack: number;
  };
  dependenciesAtRisk: {
    total: number;
    critical: number;
    high: number;
  };
}

export interface CategoryCompliance {
  business: { actual: number; target: number; status: 'compliant' | 'non_compliant'; trend: 'up' | 'down' | 'stable'; };
  tech: { actual: number; target: number; status: 'compliant' | 'non_compliant'; trend: 'up' | 'down' | 'stable'; };
  support: { actual: number; target: number; status: 'compliant' | 'non_compliant'; trend: 'up' | 'down' | 'stable'; };
  overallCompliance: number;
  complianceScore: number;
}

export interface QuarterlyReadiness {
  quarter: number;
  readyPercentage: number;
  inRefinementPercentage: number;
  notReadyPercentage: number;
  totalFeatures: number;
}

// DataTable Column type
export interface Column<T> {
  key: keyof T | string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  render?: (value: any, row: T, index: number) => React.ReactNode;
  className?: string;
}

// Cost Management Enums
export enum CostCategory {
  LABOR = 'LABOR',
  SOFTWARE_LICENSES = 'SOFTWARE_LICENSES',
  INFRASTRUCTURE = 'INFRASTRUCTURE',
  TRAINING = 'TRAINING',
  TRAVEL = 'TRAVEL',
  CONTRACTOR_SERVICES = 'CONTRACTOR_SERVICES',
  OTHER = 'OTHER',
}

// Cost Management Types
export interface ProgramBudget {
  annualBudget: number | null;
  operationalOverhead: number | null;
  laborBudget: number | null;
  nonLaborBudget: number | null;
  budgetFiscalYear: number | null;
}

export interface OperationalCostEntry {
  id: string;
  programId: string;
  category: CostCategory;
  description: string;
  amount: number;
  fiscalYear: number;
  quarter: number;
  month: number | null;
  enteredById: string;
  enteredBy?: User;
  createdAt: string;
  updatedAt: string;
}

export interface ProgramBudgetResponse {
  program: {
    id: string;
    name: string;
  };
  budget: {
    annual: number | null;
    labor: number | null;
    nonLabor: number | null;
    operationalOverheadPercent: number | null;
    operationalOverheadAmount: number | null;
    deliveryBudget: number | null;
  };
  committed: {
    totalCost: number;
    teamWeeks: number;
    allocationCount: number;
    epicCount: number;
    committedPercent: number;
    budgetAfterCommitted: number;
  };
  actuals: {
    laborCost: number;
    nonLaborCost: number;
    totalCost: number;
  };
  remaining: {
    total: number | null;
    labor: number | null;
    nonLabor: number | null;
  };
  utilizationPercent: number | null;
  calculatedTeamCost?: {
    totalAnnualCost: number;
    totalPlannedCost: number;
    teamCount: number;
    fiscalYear: number;
    teamBreakdown: Array<{
      teamId: string;
      teamName: string;
      memberCount: number;
      annualCost: number;
      plannedCost: number;
      costProfileName: string;
    }>;
  } | null;
  variance?: {
    laborBudgetVsCalculated: number;
    laborBudgetVsCalculatedPercent: number;
    status: 'UNDER_BUDGET' | 'ON_BUDGET' | 'OVER_BUDGET';
  } | null;
}

export interface ProgramCostSummary {
  programId: string;
  programName: string;
  totalCosts: number;
  costsByCategory: Record<CostCategory, number>;
  costsByQuarter: Record<number, number>;
  budgetUtilization: number | null;
}

export interface DivisionCostSummary {
  divisionId: string;
  divisionName: string;
  totalCosts: number;
  programCount: number;
  programs: ProgramCostSummary[];
  costsByCategory: Record<CostCategory, number>;
}

export interface CostDashboardData {
  totalCosts: number;
  totalBudget: number;
  utilizationPercent: number;
  divisions: DivisionCostSummary[];
  costsByCategory: Record<CostCategory, number>;
  costsByQuarter: Record<number, number>;
}

// ============================================================================
// Scenario Optimization Types
// ============================================================================

/**
 * Constraints for the optimization algorithm
 */
export interface OptimizationConstraints {
  maxTeamUtilization: number;     // e.g., 0.85 (85%)
  minBusinessAllocation: number;   // e.g., 0.75 (75%)
  minTechAllocation: number;       // e.g., 0.15 (15%)
  minSupportAllocation: number;    // e.g., 0.10 (10%)
}

/**
 * EPIC priority for optimization ordering
 */
export interface EpicPriority {
  epicId: string;
  priority: number; // 1-10, higher = more important
}

/**
 * Input for running an optimization
 */
export interface OptimizationInput {
  scenarioId: string;
  planningPeriodId: string;
  constraints: OptimizationConstraints;
  priorities: EpicPriority[];
}

/**
 * A single allocation suggestion from the optimizer
 */
export interface AllocationSuggestion {
  epicId: string;
  epicName: string;
  programId: string;
  programName: string;
  teamId: string;
  teamName: string;
  allocatedWeeks: number;
  reason: string;
}

/**
 * Tracks demand that could not be met
 */
export interface UnmetDemandItem {
  epicId: string;
  epicName: string;
  isCategory: ISCategory;
  requested: number;
  allocated: number;
  gap: number;
}

/**
 * Metrics from optimization analysis
 */
export interface OptimizationMetrics {
  totalDemand: number;
  totalSupply: number;
  utilizationRate: number;
  categoryAllocation: {
    BUSINESS: number;
    TECH: number;
    SUPPORT: number;
  };
  unmetDemand: UnmetDemandItem[];
}

/**
 * Result of an optimization run
 */
export interface OptimizationResult {
  feasible: boolean;
  message: string;
  allocations: AllocationSuggestion[];
  metrics: OptimizationMetrics;
  warnings: string[];
}

/**
 * Team capacity information
 */
export interface TeamCapacity {
  teamId: string;
  teamName: string;
  programId: string;
  programName: string;
  totalWeeks: number;
  operationalOverhead: number;
  availableWeeks: number;
  allocatedWeeks: number;
  remainingWeeks: number;
  utilizationPercent: number;
  memberCount?: number;
}

/**
 * Program supply breakdown
 */
export interface ProgramSupply {
  programId: string;
  programName: string;
  category: ISCategory;
  totalWeeks: number;
  availableWeeks: number;
  allocatedWeeks: number;
  remainingWeeks: number;
  teams: TeamCapacity[];
}

/**
 * Category supply breakdown
 */
export interface CategorySupply {
  category: ISCategory;
  totalWeeks: number;
  availableWeeks: number;
  allocatedWeeks: number;
}

/**
 * EPIC demand breakdown
 */
export interface EpicDemand {
  epicId: string;
  epicName: string;
  requestedWeeks: number;
  allocatedWeeks: number;
  gap: number;
  priority: number | null;
}

/**
 * Investible Structure demand breakdown
 */
export interface ISDemand {
  isId: string;
  isName: string;
  category: ISCategory;
  requestedWeeks: number;
  allocatedWeeks: number;
  gap: number;
  epics: EpicDemand[];
}

/**
 * Category demand breakdown
 */
export interface CategoryDemand {
  category: ISCategory;
  requestedWeeks: number;
  allocatedWeeks: number;
  targetPercent: number;
  actualPercent: number;
}

/**
 * Category compliance status
 */
export interface CategoryComplianceStatus {
  targetPercent: number;
  actualPercent: number;
  compliant: boolean;
  deviation: number;
}

/**
 * Supply vs Demand analysis result
 */
export interface SupplyDemandAnalysis {
  scenarioId: string;
  scenarioName: string;
  planningPeriodId: string;
  supply: {
    totalTeamWeeks: number;
    byProgram: ProgramSupply[];
    byCategory: CategorySupply[];
  };
  demand: {
    totalTeamWeeks: number;
    byInvestibleStructure: ISDemand[];
    byCategory: CategoryDemand[];
  };
  gap: {
    totalGap: number;
    utilizationRate: number;
    status: 'UNDER_CAPACITY' | 'NEAR_CAPACITY' | 'OVER_CAPACITY';
  };
  categoryCompliance: {
    BUSINESS: CategoryComplianceStatus;
    TECH: CategoryComplianceStatus;
    SUPPORT: CategoryComplianceStatus;
  };
}

/**
 * Team capacity response from API
 */
export interface TeamCapacityResponse {
  scenarioId: string;
  scenarioName: string;
  summary: {
    totalTeams: number;
    totalCapacity: number;
    totalAllocated: number;
    remainingCapacity: number;
    averageUtilization: number;
  };
  byProgram: Array<{
    programId: string;
    programName: string;
    totalCapacity: number;
    totalAllocated: number;
    utilizationPercent: number;
    teams: TeamCapacity[];
  }>;
  teams: TeamCapacity[];
}

/**
 * Scenario summary for comparison
 */
export interface ScenarioSummary {
  id: string;
  name: string;
  isBaseline: boolean;
  metrics: {
    totalDemand: number;
    totalSupply: number;
    totalAllocated: number;
    utilizationRate: number;
    totalCost: number;
    epicCount: number;
    allocationCount: number;
    categoryAllocation: {
      BUSINESS: number;
      TECH: number;
      SUPPORT: number;
    };
  };
}

/**
 * EPIC allocation change between scenarios
 */
export interface EpicAllocationChange {
  epicId: string;
  epicName: string;
  scenario1Allocation: number;
  scenario2Allocation: number;
  difference: number;
}

/**
 * Differences between two scenarios
 */
export interface ScenarioDifferences {
  demandDiff: number;
  supplyDiff: number;
  allocatedDiff: number;
  utilizationDiff: number;
  costDiff: number;
  epicCountDiff: number;
  allocationCountDiff: number;
  categoryChanges: {
    BUSINESS: number;
    TECH: number;
    SUPPORT: number;
  };
  epicAllocationChanges: EpicAllocationChange[];
}

/**
 * Scenario comparison result
 */
export interface ScenarioComparison {
  scenario1: ScenarioSummary;
  scenario2: ScenarioSummary;
  differences: ScenarioDifferences;
  samePlanningPeriod?: boolean;
  warning?: string;
}

// ============================================================================
// Budget Analysis Types
// ============================================================================

export interface BudgetTeamCostDetail {
  teamId: string;
  teamName: string;
  memberCount: number;
  adjustedMemberCount: number | null;
  annualCost: number;
  costPerTeamWeek: number;
  isAdjusted: boolean;
  isReleased?: boolean;
  isVirtual?: boolean;
  costProfileId?: string;
  costProfileName?: string;
  adjustedCostProfileId?: string;
  adjustedCostProfileName?: string;
}

export interface BudgetTeamCostProjection {
  totalAnnualCost: number;
  monthlyRunRate: number;
  teams: BudgetTeamCostDetail[];
}

export interface BudgetFinance {
  laborBudget: number;
  nonLaborBudget: number;
  totalAnnualBudget: number;
  operationalOverhead: number;
}

export interface BudgetEpicDemandDetail {
  epicId: string;
  epicName: string;
  requestedTeamWeeks: number;
  adjustedTeamWeeks: number | null;
  estimatedCost: number;
  isAdjusted: boolean;
}

export interface BudgetISDemandDetail {
  isId: string;
  isName: string;
  category: string;
  totalCost: number;
  epics: BudgetEpicDemandDetail[];
}

export interface BudgetISDemand {
  totalDemandCost: number;
  investibleStructures: BudgetISDemandDetail[];
}

export interface BudgetVariance {
  teamCostVsBudget: number;
  teamCostVsBudgetPercent: number;
  demandVsBudget: number;
  demandVsBudgetPercent: number;
  status: 'UNDER_BUDGET' | 'NEAR_BUDGET' | 'OVER_BUDGET';
}

export interface BudgetAnalysisResult {
  scenarioId: string;
  programId: string;
  programName: string;
  fiscalYear: number;
  teamCostProjection: BudgetTeamCostProjection;
  financeBudget: BudgetFinance;
  isDemand: BudgetISDemand;
  variance: BudgetVariance;
}

// ============================================================================
// Recommendation Types
// ============================================================================

export interface RollOffSuggestion {
  epicId: string;
  epicName: string;
  investibleStructureName: string;
  category: string;
  priority: number;
  allocatedTeamWeeks: number;
  costSaved: number;
  impact: string;
}

export interface OverCommittedRecommendation {
  teamId: string;
  teamName: string;
  programId: string;
  programName: string;
  currentUtilization: number;
  targetUtilization: number;
  excessWeeks: number;
  rollOffSuggestions: RollOffSuggestion[];
  totalPotentialSavings: number;
}

export interface TeamAdditionOption {
  costProfileId: string;
  costProfileName: string;
  hourlyRate: number;
  additionalMembers: number;
  additionalCapacityWeeks: number;
  additionalAnnualCost: number;
}

export interface NewTeamOption {
  costProfileId: string;
  costProfileName: string;
  hourlyRate: number;
  suggestedTeamSize: number;
  capacityWeeks: number;
  annualCost: number;
}

export interface UnderUtilizedRecommendation {
  teamId: string;
  teamName: string;
  programId: string;
  programName: string;
  currentUtilization: number;
  spareCapacityWeeks: number;
  addMemberOptions: TeamAdditionOption[];
  newTeamOptions: NewTeamOption[];
}

export interface EpicBudgetFit {
  epicId: string;
  epicName: string;
  category: string;
  priority: number;
  requestedTeamWeeks: number;
  estimatedCost: number;
}

export interface CostProfileAlternative {
  epicId: string;
  epicName: string;
  currentCost: number;
  onshoreCost: number;
  offshoreCost: number;
  blendedCost: number;
  recommendation: string;
}

export interface BudgetFitRecommendation {
  programId: string;
  programName: string;
  laborBudget: number;
  currentTeamCost: number;
  budgetHeadroom: number;
  fitsWithinBudget: EpicBudgetFit[];
  doesNotFit: EpicBudgetFit[];
  costProfileAlternatives: CostProfileAlternative[];
}

export interface RecommendationsResult {
  scenarioId: string;
  programId: string | null;
  overCommitted: OverCommittedRecommendation[];
  underUtilized: UnderUtilizedRecommendation[];
  budgetFit: BudgetFitRecommendation | null;
  summary: {
    overCommittedTeamCount: number;
    underUtilizedTeamCount: number;
    totalPotentialSavings: number;
    budgetStatus: string | null;
  };
}

// ============================================================================
// Scenario Virtual Team Types
// ============================================================================

export interface VirtualTeamAllocation {
  id: string;
  scenarioId: string;
  virtualTeamId: string;
  epicId: string;
  teamWeeks: number;
  costAmount?: number;
  notes?: string;
  epic?: { id: string; name: string };
}

export interface VirtualTeam {
  id: string;
  scenarioId: string;
  programId: string;
  name: string;
  costProfileId: string;
  memberCount: number;
  notes?: string;
  costProfile?: { id: string; name: string; hourlyRate: number };
  allocations?: VirtualTeamAllocation[];
}

// Re-export search types
export * from './search';
